import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Descriptions } from '@arco-design/web-react';

import { HealthCheckPreviewSection } from '@/delivery-type-plugin/capabilities/health-check';
import {
  hasMeaningfulHealthCheckConfig,
  readHealthCheckFromFormValues,
} from '@/delivery-type-plugin/capabilities/health-check/healthCheckForm';
import type { MaasGroupSpec } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { getMaasGroupImageDisplay } from '@/delivery-type-plugin/capabilities/maas-init/utils/display';
import type { EmbeddedNodeSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { resolveEmbeddedHealthCheckEnabled } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';

import styles from './group-spec-card.module.less';

export interface GroupSpecEmbeddedPreviewProps {
  spec?: MaasGroupSpec;
  /** 训练集群：仅展示训练镜像标签 */
  trainingImageOnly?: boolean;
}

/** 节点卡片内嵌配置摘要：镜像 / 探活（共享内存、网卡已并入资源 metrics） */
export function GroupSpecEmbeddedPreview({ spec, trainingImageOnly }: GroupSpecEmbeddedPreviewProps) {
  const { t } = useTranslation();
  if (!spec) return null;

  const system = spec.system as Record<string, unknown> | undefined;
  const image = getMaasGroupImageDisplay(system);
  const embeddedUi = spec as EmbeddedNodeSpec;
  const healthCheck = readHealthCheckFromFormValues({
    _containerDeployHealthCheck: embeddedUi._containerDeployHealthCheck,
  });
  const showHealthCheck =
    resolveEmbeddedHealthCheckEnabled(embeddedUi) && (hasMeaningfulHealthCheckConfig(healthCheck) || !!system?.livenessProbe);

  const items: { label: string; value: ReactNode; span?: number }[] = [];
  if (image) {
    items.push({
      label: trainingImageOnly ? t('data-center:maas.inference.custom.image') : t('data-center:depend.inference.framework'),
      value: image,
    });
  }

  if (!items.length && !showHealthCheck) return null;

  return (
    <div className={styles.embeddedPreview}>
      {items.length ? <Descriptions column={2} data={items} border size="small" style={{ marginBottom: showHealthCheck ? 8 : 0 }} /> : null}
      {showHealthCheck ? (
        <div>
          <div className={styles.embeddedPreviewLabel}>{t('data-center:probe')}</div>
          <HealthCheckPreviewSection
            healthCheck={healthCheck}
            livenessProbe={system?.livenessProbe as Record<string, unknown> | undefined}
          />
        </div>
      ) : null}
    </div>
  );
}
